/**
 * ESC/POS, the small subset a receipt and a kitchen ticket need.
 *
 * Everything here builds a plain string rather than a byte array, because QZ
 * sends `flavor: 'plain'` straight through to the printer — the same way the
 * receipt has always been built. Keeping one representation means the receipt
 * and the ticket can share the wrapping and column helpers below.
 */

export const ESC = '\x1B';
export const GS = '\x1D';

/** Clears whatever the previous job left set — always the first bytes out. */
export const INIT = ESC + '@';

export const ALIGN_LEFT = ESC + 'a' + '\x00';
export const ALIGN_CENTER = ESC + 'a' + '\x01';
export const ALIGN_RIGHT = ESC + 'a' + '\x02';

export const BOLD_ON = ESC + 'E' + '\x01';
export const BOLD_OFF = ESC + 'E' + '\x00';

/**
 * `ESC ! n` sets everything about the character in one byte: bit 3 bold,
 * bit 4 double height, bit 5 double width. Chosen over `GS !` because more of
 * the cheap 80mm printers in the field understand it.
 */
export const SIZE_NORMAL = ESC + '!' + '\x00';
export const SIZE_DOUBLE = ESC + '!' + '\x30'; // double height AND width
export const SIZE_DOUBLE_BOLD = ESC + '!' + '\x38'; // the above, plus bold
export const SIZE_TALL = ESC + '!' + '\x10'; // double height only

export const CUT = GS + 'V' + '\x00';

/**
 * Blank lines fed before the cut.
 *
 * The cutter sits a couple of centimetres above the print head, so whatever
 * is still between them is what gets sliced. Three lines — what the kitchen
 * ticket used at first — cut straight through the time at the bottom. Seven
 * is what the customer receipt has always used on this hardware, so it is
 * the number both now share.
 */
export const FEED_BEFORE_CUT = 7;

/** Characters per line at font A on 80mm paper. The phone uses the same. */
export const WIDTH = 48;

export const feed = (lines = 1) => '\n'.repeat(Math.max(0, lines));

export const rule = (character = '-', width = WIDTH) => character.repeat(width) + '\n';

export const line = (text = '') => String(text) + '\n';

/** Left text and right text pushed to the edges of one line. */
export function leftRight(left, right, width = WIDTH) {
  const a = String(left);
  const b = String(right);
  const gap = Math.max(1, width - a.length - b.length);
  return a + ' '.repeat(gap) + b + '\n';
}

/**
 * Hard-wrap to the paper width.
 *
 * Thermal printers wrap mid-word on their own, which splits Somali item names
 * in unreadable places — `Bariis Iskuk` / `aris`. Wrapping on spaces first and
 * only breaking a word that is genuinely too long fixes that.
 */
export function wrap(text, width) {
  if (!width || width <= 0) return [String(text || '')];

  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';

  words.forEach((word) => {
    if (!current) current = word;
    else if (current.length + 1 + word.length <= width) current = `${current} ${word}`;
    else {
      lines.push(current);
      current = word;
    }

    while (current.length > width) {
      lines.push(current.slice(0, width));
      current = current.slice(width);
    }
  });

  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

/** `Chicken Bur...` — cut with an ellipsis rather than clipped mid-letter. */
export function truncate(text, maxLength) {
  const value = String(text || '');
  if (value.length <= maxLength) return value;
  return value.slice(0, Math.max(0, maxLength - 3)) + '...';
}
